import React, { Component } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
	Row,
	Col,
	Form,
	FormGroup,
	Label,
	Input,
	Button,
	Badge,
	Table,
	Spinner,
	Alert
} from 'reactstrap';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	ArcElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
	Filler,
} from 'chart.js';
import Api from '../../helper/Api';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import './AdvertisementAnalytics.css';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	ArcElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
	Filler
);

class AdvertisementAnalytics extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			analyticsData: null,
			error: '',
			generatingPdf: false,
			filters: {
				eventId: '',
				advertisementId: '',
				startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
				endDate: moment().format('YYYY-MM-DD'),
				groupBy: 'day'
			},
			events: [],
			advertisements: []
		};
	}

	componentDidMount() {
		this.loadEvents();
		this.loadAdvertisements();
		this.loadAnalytics();
	}

	loadEvents = async () => {
		try {
			const params = {
				sort_by: 'eventName',
				order: 'ASC',
				search_event: '',
				search_user: '',
				page: '1'
			};
			const response = await Api('POST', 'cms/event/list', params);
			if (response.status === 200 && response.data && response.data.data) {
				// The API returns data.rows, not data.events
				this.setState({ events: response.data.data.rows || [] });
			}
		} catch (error) {
			console.error('Error loading events:', error);
			// Fallback: Set empty events array if loading fails
			this.setState({ events: [] });
		}
	};

	loadAdvertisements = async () => {
		try {
			const params = {
				page: 1,
				limit: 100,
				search: ''
			};
			const response = await Api('GET', 'cms/advertisement', params);
			if (response.status === 200 && response.data && response.data.data) {
				this.setState({ advertisements: response.data.data.advertisements || [] });
			}
		} catch (error) {
			console.error('Error loading advertisements:', error);
			// Fallback: Set empty advertisements array if loading fails
			this.setState({ advertisements: [] });
		}
	};

	loadAnalytics = async () => {
		this.setState({ loading: true, error: '' });

		try {
			const params = new URLSearchParams();
			Object.keys(this.state.filters).forEach(key => {
				if (this.state.filters[key]) {
					params.append(key, this.state.filters[key]);
				}
			});

			const response = await Api('GET', 'cms/advertisement/analytics/views?' + params.toString());
			
			if (response.status === 200 && response.data && response.data.data) {
				this.setState({ analyticsData: response.data.data });
			} else {
				this.setState({ error: (response.data && response.data.message) ? response.data.message : 'Failed to load analytics data' });
			}
		} catch (error) {
			console.error('Error loading analytics:', error);
			const errorMsg = error.response && error.response.data && error.response.data.message 
				? error.response.data.message 
				: error.message;
			this.setState({ error: 'Error loading analytics data: ' + errorMsg });
		} finally {
			this.setState({ loading: false });
		}
	};

	handleFilterChange = (field, value) => {
		this.setState(prevState => ({
			filters: {
				...prevState.filters,
				[field]: value
			}
		}));
	};

	handleApplyFilters = () => {
		this.loadAnalytics();
	};

	resetFilters = () => {
		this.setState({
			filters: {
				eventId: '',
				advertisementId: '',
				startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
				endDate: moment().format('YYYY-MM-DD'),
				groupBy: 'day'
			}
		}, () => {
			this.loadAnalytics();
		});
	};

	generateChartData = () => {
		const { analyticsData } = this.state;
		if (!analyticsData || !analyticsData.analytics) return null;

		const { analytics } = analyticsData;
		

		// Time series data
		const timeSeriesData = {
			labels: analytics.timeSeriesData ? analytics.timeSeriesData.map(item => item.period) : [],
			datasets: [
				{
					label: 'Views',
					data: analytics.timeSeriesData ? analytics.timeSeriesData.map(item => item.views) : [],
					borderColor: 'rgb(75, 192, 192)',
					backgroundColor: 'rgba(75, 192, 192, 0.1)',
					fill: true,
				},
			],
		};

		// Context distribution
		const contextData = {
			labels: Object.keys(analytics.contextStats || {}),
			datasets: [
				{
					data: Object.values(analytics.contextStats || {}),
					backgroundColor: [
						'rgba(255, 99, 132, 0.8)',
						'rgba(54, 162, 235, 0.8)',
						'rgba(255, 205, 86, 0.8)',
						'rgba(75, 192, 192, 0.8)',
						'rgba(153, 102, 255, 0.8)',
					],
				},
			],
		};

		// Device distribution
		const deviceData = {
			labels: Object.keys(analytics.deviceStats || {}),
			datasets: [
				{
					data: Object.values(analytics.deviceStats || {}),
					backgroundColor: [
						'rgba(255, 159, 64, 0.8)',
						'rgba(255, 99, 132, 0.8)',
						'rgba(54, 162, 235, 0.8)',
					],
				},
			],
		};

		// Event distribution
		const eventData = {
			labels: analytics.eventStats ? analytics.eventStats.map(event => event.name) : [],
			datasets: [
				{
					data: analytics.eventStats ? analytics.eventStats.map(event => event.count) : [],
					backgroundColor: [
						'rgba(102, 126, 234, 0.8)',
						'rgba(118, 75, 162, 0.8)',
						'rgba(92, 184, 92, 0.8)',
						'rgba(91, 192, 222, 0.8)',
						'rgba(240, 173, 78, 0.8)',
						'rgba(217, 83, 79, 0.8)',
						'rgba(153, 102, 255, 0.8)',
						'rgba(255, 159, 64, 0.8)',
					],
				},
			],
		};

		return { timeSeriesData, contextData, deviceData, eventData };
	};

	exportToPDF = async () => {
		const { analyticsData, filters, generatingPdf } = this.state;
		
		if (generatingPdf) return;
		
		this.setState({ generatingPdf: true });

		try {
			const pdf = new jsPDF('p', 'mm', 'a4');
			const pageWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();
			let currentY = 20;

			// Title
			pdf.setFontSize(20);
			pdf.setFont('helvetica', 'bold');
			pdf.text('Advertisement Analytics Report', pageWidth / 2, currentY, { align: 'center' });
			currentY += 15;

			// Date range
			pdf.setFontSize(12);
			pdf.setFont('helvetica', 'normal');
			const dateRange = `Period: ${filters.startDate || 'All'} to ${filters.endDate || 'All'}`;
			pdf.text(dateRange, pageWidth / 2, currentY, { align: 'center' });
			currentY += 10;

			// Filter info
			const filterInfo = [];
			if (filters.eventId) {
				const event = this.state.events.find(e => e.id.toString() === filters.eventId);
				filterInfo.push(`Event: ${event ? event.eventName : 'Unknown'}`);
			}
			if (filters.advertisementId) {
				const ad = this.state.advertisements.find(a => a.id === filters.advertisementId);
				filterInfo.push(`Advertisement: ${ad ? ad.title : 'Unknown'}`);
			}
			if (filterInfo.length > 0) {
				pdf.text(`Filters: ${filterInfo.join(', ')}`, pageWidth / 2, currentY, { align: 'center' });
				currentY += 15;
			} else {
				currentY += 5;
			}

			// Summary statistics
			if (analyticsData && analyticsData.analytics) {
				const { analytics } = analyticsData;
				
				pdf.setFontSize(14);
				pdf.setFont('helvetica', 'bold');
				pdf.text('Summary Statistics', 20, currentY);
				currentY += 10;

				const summaryData = [
					['Total Views', (analyticsData.totalViews && analyticsData.totalViews.toString()) || '0'],
					['Advertisements Viewed', (analytics.advertisementStats && analytics.advertisementStats.length && analytics.advertisementStats.length.toString()) || '0'],
					['Events with Views', analytics.eventStats ? analytics.eventStats.length.toString() : '0'],
					['Device Types', Object.keys(analytics.deviceStats || {}).length.toString()],
					['View Contexts', Object.keys(analytics.contextStats || {}).length.toString()]
				];

				pdf.autoTable({
					startY: currentY,
					head: [['Metric', 'Value']],
					body: summaryData,
					theme: 'grid',
					headStyles: { fillColor: [102, 126, 234] },
					margin: { left: 20, right: 20 },
				});

				currentY = pdf.lastAutoTable.finalY + 15;

				// Top Advertisements table
				if (analytics.advertisementStats && analytics.advertisementStats.length > 0) {
					pdf.setFontSize(14);
					pdf.setFont('helvetica', 'bold');
					pdf.text('Top Advertisements', 20, currentY);
					currentY += 10;

					const adData = analytics.advertisementStats.slice(0, 10).map((ad, index) => [
						`#${index + 1}`,
						ad.title || 'Unknown',
						(ad.count && ad.count.toString()) || '0'
					]);

					pdf.autoTable({
						startY: currentY,
						head: [['Rank', 'Advertisement', 'Views']],
						body: adData,
						theme: 'striped',
						headStyles: { fillColor: [92, 184, 92] },
						margin: { left: 20, right: 20 },
					});

					currentY = pdf.lastAutoTable.finalY + 15;
				}

				// Views by Event table
				if (analytics.eventStats && analytics.eventStats.length > 0) {
					// Check if we need a new page
					if (currentY > pageHeight - 80) {
						pdf.addPage();
						currentY = 20;
					}

					pdf.setFontSize(14);
					pdf.setFont('helvetica', 'bold');
					pdf.text('Views by Event', 20, currentY);
					currentY += 10;

					const eventData = analytics.eventStats.slice(0, 10).map((event, index) => [
						`#${index + 1}`,
						event.name || 'Unknown',
						(event.count && event.count.toString()) || '0'
					]);

					pdf.autoTable({
						startY: currentY,
						head: [['Rank', 'Event', 'Views']],
						body: eventData,
						theme: 'striped',
						headStyles: { fillColor: [91, 192, 222] },
						margin: { left: 20, right: 20 },
					});

					currentY = pdf.lastAutoTable.finalY + 15;
				}

				// Context and Device breakdowns
				if (Object.keys(analytics.contextStats || {}).length > 0 || Object.keys(analytics.deviceStats || {}).length > 0) {
					// Check if we need a new page
					if (currentY > pageHeight - 100) {
						pdf.addPage();
						currentY = 20;
					}

					pdf.setFontSize(14);
					pdf.setFont('helvetica', 'bold');
					pdf.text('View Context & Device Breakdown', 20, currentY);
					currentY += 10;

					const contextData = Object.entries(analytics.contextStats || {}).map(([context, count]) => [
						'Context',
						context,
						count.toString()
					]);

					const deviceData = Object.entries(analytics.deviceStats || {}).map(([device, count]) => [
						'Device',
						device,
						count.toString()
					]);

					const combinedData = [...contextData, ...deviceData];

					if (combinedData.length > 0) {
						pdf.autoTable({
							startY: currentY,
							head: [['Type', 'Category', 'Views']],
							body: combinedData,
							theme: 'grid',
							headStyles: { fillColor: [240, 173, 78] },
							margin: { left: 20, right: 20 },
						});
					}
				}
			}

			// Footer
			const totalPages = pdf.internal.getNumberOfPages();
			for (let i = 1; i <= totalPages; i++) {
				pdf.setPage(i);
				pdf.setFontSize(8);
				pdf.setFont('helvetica', 'normal');
				pdf.text(`Generated on ${moment().format('YYYY-MM-DD HH:mm:ss')} - Page ${i} of ${totalPages}`, 
					pageWidth / 2, pageHeight - 10, { align: 'center' });
			}

			// Save the PDF
			const filename = `advertisement-analytics-${moment().format('YYYY-MM-DD-HHmmss')}.pdf`;
			pdf.save(filename);

		} catch (error) {
			console.error('Error generating PDF:', error);
			alert('Failed to generate PDF report. Please try again.');
		} finally {
			this.setState({ generatingPdf: false });
		}
	};


	render() {
		const { loading, analyticsData, error, filters, events, advertisements, generatingPdf } = this.state;
		const chartData = this.generateChartData();

		const timeSeriesOptions = {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: {
					position: 'top',
				},
				title: {
					display: true,
					text: 'Advertisement Views Over Time',
				},
			},
			scales: {
				y: {
					beginAtZero: true,
					ticks: {
						stepSize: 1,
					},
				},
			},
		};

		const pieOptions = {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: {
					position: 'right',
				},
			},
		};

		return (
			<div className="advertisement-analytics">
				{/* Filters */}
				<Card className="mb-4">
					<CardHeader>
						<h5 className="mb-0">
							<i className="fas fa-filter mr-2"></i>
							Analytics Filters
						</h5>
					</CardHeader>
					<CardBody>
						<Form>
							<Row>
								<Col md={3}>
									<FormGroup>
										<Label for="eventFilter">Event</Label>
										<Input
											type="select"
											id="eventFilter"
											value={filters.eventId}
											onChange={(e) => this.handleFilterChange('eventId', e.target.value)}
										>
											<option value="">All Events</option>
											{events.map(event => (
												<option key={event.id} value={event.id}>
													{event.eventName}
												</option>
											))}
										</Input>
									</FormGroup>
								</Col>
								<Col md={3}>
									<FormGroup>
										<Label for="adFilter">Advertisement</Label>
										<Input
											type="select"
											id="adFilter"
											value={filters.advertisementId}
											onChange={(e) => this.handleFilterChange('advertisementId', e.target.value)}
										>
											<option value="">All Advertisements</option>
											{advertisements.map(ad => (
												<option key={ad.id} value={ad.id}>
													{ad.title}
												</option>
											))}
										</Input>
									</FormGroup>
								</Col>
								<Col md={2}>
									<FormGroup>
										<Label for="startDate">Start Date</Label>
										<Input
											type="date"
											id="startDate"
											value={filters.startDate}
											onChange={(e) => this.handleFilterChange('startDate', e.target.value)}
										/>
									</FormGroup>
								</Col>
								<Col md={2}>
									<FormGroup>
										<Label for="endDate">End Date</Label>
										<Input
											type="date"
											id="endDate"
											value={filters.endDate}
											onChange={(e) => this.handleFilterChange('endDate', e.target.value)}
										/>
									</FormGroup>
								</Col>
								<Col md={2}>
									<FormGroup>
										<Label for="groupBy">Group By</Label>
										<Input
											type="select"
											id="groupBy"
											value={filters.groupBy}
											onChange={(e) => this.handleFilterChange('groupBy', e.target.value)}
										>
											<option value="hour">Hour</option>
											<option value="day">Day</option>
											<option value="week">Week</option>
											<option value="month">Month</option>
										</Input>
									</FormGroup>
								</Col>
							</Row>
							<Row>
								<Col>
									<Button color="primary" onClick={this.handleApplyFilters} disabled={loading}>
										<i className="fas fa-search mr-2"></i>
										Apply Filters
									</Button>
									<Button color="secondary" onClick={this.resetFilters} className="ml-2">
										<i className="fas fa-undo mr-2"></i>
										Reset
									</Button>
									{analyticsData && (
										<Button color="success" onClick={this.exportToPDF} className="ml-2" disabled={generatingPdf}>
											<i className={`fas ${generatingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'} mr-2`}></i>
											{generatingPdf ? 'Generating...' : 'Export PDF'}
										</Button>
									)}
								</Col>
							</Row>
						</Form>
					</CardBody>
				</Card>

				{error && (
					<Alert color="danger" className="mb-4">
						{error}
					</Alert>
				)}

				{loading ? (
					<div className="text-center py-5">
						<Spinner color="primary" size="lg" />
						<p className="mt-2">Loading analytics...</p>
					</div>
				) : analyticsData ? (
					<React.Fragment>
						{/* Summary Cards */}
						<Row className="mb-4">
							<Col md={3}>
								<Card className="bg-primary text-white">
									<CardBody className="text-center">
										<h2 className="mb-0">{analyticsData.totalViews}</h2>
										<p className="mb-0">Total Views</p>
									</CardBody>
								</Card>
							</Col>
							<Col md={3}>
								<Card className="bg-success text-white">
									<CardBody className="text-center">
										<h2 className="mb-0">
											{analyticsData.analytics && analyticsData.analytics.advertisementStats 
												? analyticsData.analytics.advertisementStats.length 
												: 0}
										</h2>
										<p className="mb-0">Advertisements Viewed</p>
									</CardBody>
								</Card>
							</Col>
							<Col md={3}>
								<Card className="bg-info text-white">
									<CardBody className="text-center">
										<h2 className="mb-0">
											{analyticsData.analytics 
												? Object.keys(analyticsData.analytics.deviceStats || {}).length 
												: 0}
										</h2>
										<p className="mb-0">Device Types</p>
									</CardBody>
								</Card>
							</Col>
							<Col md={3}>
								<Card className="bg-warning text-white">
									<CardBody className="text-center">
										<h2 className="mb-0">
											{analyticsData.analytics 
												? Object.keys(analyticsData.analytics.contextStats || {}).length 
												: 0}
										</h2>
										<p className="mb-0">View Contexts</p>
									</CardBody>
								</Card>
							</Col>
						</Row>

						{/* Charts */}
						<Row className="mb-4">
							<Col md={8}>
								<Card>
									<CardHeader>
										<h5 className="mb-0">Views Over Time</h5>
									</CardHeader>
									<CardBody>
										<div style={{ height: '300px' }}>
											{chartData && chartData.timeSeriesData && (
												<Line data={chartData.timeSeriesData} options={timeSeriesOptions} />
											)}
										</div>
									</CardBody>
								</Card>
							</Col>
							<Col md={4}>
								<Card>
									<CardHeader>
										<h5 className="mb-0">Views by Context</h5>
									</CardHeader>
									<CardBody>
										<div style={{ height: '300px' }}>
											{chartData && chartData.contextData && (
												<Pie data={chartData.contextData} options={pieOptions} />
											)}
										</div>
									</CardBody>
								</Card>
							</Col>
						</Row>

						<Row className="mb-4">
							<Col md={6}>
								<Card>
									<CardHeader>
										<h5 className="mb-0">Views by Event</h5>
									</CardHeader>
									<CardBody>
										<div style={{ height: '300px' }}>
											{chartData && chartData.eventData && chartData.eventData.labels.length > 0 ? (
												<Bar 
													data={chartData.eventData} 
													options={{
														responsive: true,
														maintainAspectRatio: false,
														plugins: {
															legend: {
																display: false,
															},
															title: {
																display: true,
																text: 'Advertisement Views by Event',
															},
														},
														scales: {
															y: {
																beginAtZero: true,
																ticks: {
																	stepSize: 1,
																},
															},
														},
													}} 
												/>
											) : (
												<div className="d-flex align-items-center justify-content-center h-100">
													<div className="text-center text-muted">
														<i className="fas fa-chart-bar fa-2x mb-2"></i>
														<p>No event data available</p>
														<small>Advertisement views need to be recorded from the mobile app to show event-based analytics</small>
													</div>
												</div>
											)}
										</div>
									</CardBody>
								</Card>
							</Col>
							<Col md={6}>
								<Card>
									<CardHeader>
										<h5 className="mb-0">Views by Device Type</h5>
									</CardHeader>
									<CardBody>
										<div style={{ height: '300px' }}>
											{chartData && chartData.deviceData && chartData.deviceData.labels.length > 0 ? (
												<Pie data={chartData.deviceData} options={pieOptions} />
											) : (
												<div className="d-flex align-items-center justify-content-center h-100">
													<div className="text-center text-muted">
														<i className="fas fa-mobile-alt fa-2x mb-2"></i>
														<p>No device data available</p>
													</div>
												</div>
											)}
										</div>
									</CardBody>
								</Card>
							</Col>
						</Row>

						<Row className="mb-4">
							<Col md={6}>
								<Card>
									<CardHeader>
										<h5 className="mb-0">Top Advertisements</h5>
									</CardHeader>
									<CardBody>
										<Table responsive size="sm">
											<thead>
												<tr>
													<th>Advertisement</th>
													<th>Views</th>
												</tr>
											</thead>
											<tbody>
												{analyticsData.analytics && analyticsData.analytics.advertisementStats 
													? analyticsData.analytics.advertisementStats.map((ad, index) => (
														<tr key={ad.id}>
															<td>
																<Badge color={index < 3 ? 'success' : 'secondary'} className="mr-2">
																	#{index + 1}
																</Badge>
																{ad.title}
															</td>
															<td>{ad.count}</td>
														</tr>
													))
													: (
														<tr>
															<td colSpan="2" className="text-center">No data available</td>
														</tr>
													)}
											</tbody>
										</Table>
									</CardBody>
								</Card>
							</Col>
							<Col md={6}>
								<Card>
									<CardHeader>
										<h5 className="mb-0">Top Events by Views</h5>
									</CardHeader>
									<CardBody>
										<Table responsive size="sm">
											<thead>
												<tr>
													<th>Event</th>
													<th>Views</th>
												</tr>
											</thead>
											<tbody>
												{analyticsData.analytics && analyticsData.analytics.eventStats 
													? analyticsData.analytics.eventStats.slice(0, 10).map((event, index) => (
														<tr key={event.id}>
															<td>
																<Badge color={index < 3 ? 'info' : 'secondary'} className="mr-2">
																	#{index + 1}
																</Badge>
																{event.name}
															</td>
															<td>{event.count}</td>
														</tr>
													))
													: (
														<tr>
															<td colSpan="2" className="text-center">No data available</td>
														</tr>
													)}
											</tbody>
										</Table>
									</CardBody>
								</Card>
							</Col>
						</Row>
					</React.Fragment>
				) : (
					<Card>
						<CardBody className="text-center py-5">
							<i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
							<h5>No Analytics Data</h5>
							<p className="text-muted">
								Apply filters above to view advertisement analytics data.
							</p>
						</CardBody>
					</Card>
				)}
			</div>
		);
	}
}

export default AdvertisementAnalytics;